import {
  combine,
  withCallbacks,
  withContext,
} from 'react-hooks-combine'
import { withFormModel } from 'shared/hooks'
import { ServicesContext } from 'services/context'
import { spec, validators, toValidatorWithArgs } from 'utils/validation'
import fp from 'lodash/fp'
import debounce from 'lodash/debounce'

import { toOptions } from 'routes/public/events/overview/components/filters/helpers'
import i18next from 'i18next'
import EditEventFormComponent from './component'
import { toEventModel } from './mapping'

const { any, string } = validators

const notEqualTeamsProp = toValidatorWithArgs(
  validators.custom.NOT_MATCH_SELECTED_TEAM_KEY,
  (propToCheck, value, { context }) => {
    const propValue = fp.get(`current.${propToCheck}`, context)
    return !value || !propValue || value.id !== propValue.id
  }
)

export const EditEventForm = combine(
  withContext(
    'taxonomyService',
    ServicesContext,
    ({ taxonomyService }) => taxonomyService
  ),

  withContext(
    'adminTournamentService',
    ServicesContext,
    ({ adminTournamentService }) => adminTournamentService
  ),

  withContext(
    'adminTeamsService',
    ServicesContext,
    ({ adminTeamsService }) => adminTeamsService
  ),

  withFormModel(
    (_state, { initialValues = {} }) => {
      return {
        eventName: {
          initialValue: initialValues.eventName || '',
          spec: spec.flow(string.isNotEmpty, any.required),
        },

        awayTeamId: {
          initialValue: initialValues.awayTeamId || {},
          spec: spec.flow(string.isNotEmpty, notEqualTeamsProp('homeTeamId')),
        },

        createTriggers: {
          initialValue: initialValues.createTriggers || false,
          spec: spec.flow(any.required),
        },

        homeTeamId: {
          initialValue: initialValues.homeTeamId || {},
          spec: spec.flow(notEqualTeamsProp('awayTeamId')),
        },

        jurisdiction: {
          initialValue: initialValues.jurisdiction || '',
          spec: spec.flow(string.isNotEmpty, any.required),
        },

        sportType: {
          initialValue: initialValues.sportType || '',
          spec: spec.flow(string.isNotEmpty, any.required),
        },

        startTime: {
          initialValue: initialValues.startTime
            ? new Date(initialValues.startTime)
            : new Date(),
          spec: spec.flow(string.isNotEmpty, any.required),
        },

        startTimeHour: {
          initialValue: initialValues.startTimeHour || '',
          spec: spec.flow(any.required),
        },

        endTime: {
          initialValue: initialValues.endTime
            ? new Date(initialValues.endTime)
            : new Date(new Date().getTime() + 2 * 60 * 60 * 1000), // Default to 2 hours after start time
          spec: spec.flow(string.isNotEmpty, any.required),
        },

        endTimeHour: {
          initialValue: initialValues.endTimeHour || '',
          spec: spec.flow(any.required),
        },

        tournamentId: {
          initialValue: initialValues.tournamentId,
          spec: spec.flow(string.isNotEmpty, any.required),
        },
      }
    },
    { id: 'event-edit-form' }
  ),

  withCallbacks(
    {
      loadJurisdictions:
        ({ taxonomyService }) =>
          (search = '') =>
            taxonomyService.queryJurisdictions({ search }),

      loadTournaments:
        ({ adminTournamentService }) => {
          // Create a debounced search function to avoid multiple requests
          const debouncedSearch = debounce(async (search, callback) => {
            try {
              const requestParams = {
                page: 0,
                size: 5,
                tournamentName: search,
                includePassedMatches: true
              };

              const { content } = await adminTournamentService.tournamentsSearch(requestParams);

              // Filter out tournaments where end date is in the past
              const now = new Date();
              const filteredContent = content ? content.filter(tournament => {
                // Check if tournament has endTime
                if (!tournament.endTime) return true;

                const endDate = new Date(tournament.endTime);
                return endDate > now;
              }) : [];

              callback(filteredContent);
            } catch (error) {
              console.error('Search error:', error);
              callback([]);
            }
          }, 300); // 300ms debounce time

          // Return a function that uses the debounced search
          return (search) => {
            return new Promise((resolve) => {
              if (!search || search.length < 2) {
                resolve([]);
                return;
              }
              debouncedSearch(search, resolve);
            });
          };
        },

      loadTeamsSearch:
        ({ adminTeamsService }) =>
          async (search, sportType) => {
            try {
              const requestParams = {
                page: 0,
                size: 5,
                teamName: search,
                sportType: sportType
              };

              const { content } = await adminTeamsService.queryTeams(requestParams);
              return content || [];
            } catch (error) {
              console.error('Team search error:', error);
              return [];
            }
          },

      loadCategories: () => async () => {
        try {
          const sportTypes = toOptions(
            i18next.t('enum:sportType', { returnObjects: true })
          )
          return sportTypes
        } catch (error) {
          console.error('Failed to load sport categories:', error)
          return []
        }
      },

      onChangeTournament: {
        deps: ['onChange'],
        func:
          ({ form }) =>
            async (tournament) => {
              // Handle null/cleared value
              if (!tournament) {
                form.fields.homeTeamId.onChange({ target: { value: null } });
                form.fields.awayTeamId.onChange({ target: { value: null } });
                form.fields.sportType.onChange({ target: { value: null } });
                form.fields.jurisdiction.onChange({ target: { value: '' } });
                return;
              }

              // If we get here, we should have a valid tournament object
              if (typeof tournament !== 'object' || !tournament.id) {
                return;
              }

              form.fields.homeTeamId.onChange({ target: { value: null } });
              form.fields.awayTeamId.onChange({ target: { value: null } });

              // Extract the sport type - check both properties that might contain it
              const sportType = tournament.sportType || tournament.sport;

              // Make sure sportType exists before proceeding
              if (!sportType) {
                return;
              }

              try {
                // Format for display (capitalize first letter, lowercase rest)
                const formattedSportType = sportType.charAt(0).toUpperCase() + sportType.slice(1).toLowerCase();

                // Create a proper option object for the select field
                const sportTypeOption = {
                  id: sportType,
                  name: formattedSportType,
                  value: sportType,
                  label: formattedSportType
                };

                // Set the value in the form
                form.fields.sportType.onChange({
                  target: { value: sportTypeOption }
                });

                // Update jurisdiction
                form.fields.jurisdiction.onChange({
                  target: { value: tournament.jurisdiction || '' },
                });

                // Ensure sportType is included in the tournament object
                const enhancedTournament = {
                  ...tournament,
                  sportType: sportType // Make sure sportType is always present
                };

                // Update tournamentId with enhanced tournament object
                form.fields.tournamentId.onChange({ target: { value: enhancedTournament } });
              } catch (error) {
                console.error('Failed to handle tournament selection:', error);
              }
            },
      },

      onSubmitRequest: {
        deps: ['onSubmit'],
        func:
          (_state, { onSubmit }) =>
            (form) => {
              // Create a copy of the form to modify
              const formData = { ...form };

              // Combine date and time for startTime
              if (formData.startTime) {
                const startDate = new Date(formData.startTime);
                if (formData.startTimeHour) {
                  const [hours, minutes] = formData.startTimeHour.split(':').map(Number);
                  startDate.setHours(hours, minutes, 0, 0);
                } else {
                  startDate.setHours(0, 0, 0, 0);
                }
                formData.startTime = startDate;
              }

              // Combine date and time for endTime
              if (formData.endTime) {
                const endDate = new Date(formData.endTime);
                if (formData.endTimeHour) {
                  const [hours, minutes] = formData.endTimeHour.split(':').map(Number);
                  endDate.setHours(hours, minutes, 0, 0);
                } else {
                  endDate.setHours(23, 59, 59, 999);
                }
                formData.endTime = endDate;
              }

              // Remove the time-only fields as they're now combined
              delete formData.startTimeHour;
              delete formData.endTimeHour;

              const mappedForm = toEventModel(formData)
              return onSubmit(mappedForm)
            },
      },
    },
    []
  )
)(EditEventFormComponent)
